import clsx from "clsx";
import React, { FunctionComponent } from "react";

import { HunterTreadDepth } from "../../queries/useGetHunterTire";
import {
  TREAD_DEPTH_CORNERS,
  TreadSeverity,
  formatTreadCorner,
  hasTreadReadings,
} from "./hunterTireFormatting";

type HunterTreadDepthCellProps = {
  tireSize: null | string;
  treadDepth: HunterTreadDepth | null | undefined;
};

/**
 * The theme exposes semantic colours only — there is no warning token — so a
 * failing corner takes `text-error` and one needing review is weighted instead
 * of being given an invented colour.
 */
const severityClass = (severity: TreadSeverity | null): string =>
  clsx({
    "font-bold": severity === "review",
    "font-bold text-error": severity === "fail",
  });

/**
 * Current tire above a 2x2 grid laid out the way the vehicle sits:
 *
 *   225/65R17
 *   FL 8/32   FR 6/32
 *   RL 2/32   RR  —
 */
export const HunterTreadDepthCell: FunctionComponent<
  HunterTreadDepthCellProps
> = ({ tireSize, treadDepth }) => {
  const hasReadings = hasTreadReadings(treadDepth);

  if (!tireSize && !hasReadings) {
    return <>-</>;
  }

  return (
    <>
      {tireSize && <div className="font-medium">{tireSize}</div>}
      {hasReadings && (
        <div className="grid grid-cols-2 gap-x-2 whitespace-nowrap">
          {TREAD_DEPTH_CORNERS.map(({ key, label, name }) => {
            const reading = formatTreadCorner(treadDepth?.[key]);
            const tooltip = [
              name,
              reading?.reported ? `reported as ${reading.reported}` : null,
              reading?.position && reading.position !== "overall"
                ? reading.position
                : null,
              reading ? null : "no reading",
            ]
              .filter(Boolean)
              .join(" — ");

            return (
              <span key={key} title={tooltip}>
                <span className="text-ink-2">{label}</span>{" "}
                {reading ? (
                  <span className={severityClass(reading.severity)}>
                    {reading.display}
                  </span>
                ) : (
                  <span className="text-ink-2">&mdash;</span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
};
